<template>
  <page layout="tabbed">
    <page-header-new :title="$t('Audiences_Title')" :breadcrumbs="breadcrumbs">
      <template slot="actions" v-if="!readonly">
        <el-button
          @click="createUserListDialog"
          type="primary"
          icon="el-icon-plus"
          v-if="currentRoute === 'AudienceUserList'"
        >
          {{ $t('Lists_CreateUserList') }}
        </el-button>

        <el-button
          @click="createOptimoveListDialog"
          type="primary"
          icon="el-icon-plus"
          v-if="currentRoute === 'AudienceOptimoveList'"
        >
          {{ $t('Lists_CreateOptimoveList') }}
        </el-button>

        <router-link
          :to="segmentCreateRoute"
          v-if="currentRoute === 'AudienceSegmentList'"
        >
          <el-button
            type="primary"
            icon="el-icon-plus"
            v-if="currentRoute === 'AudienceSegmentList'"
          >
            {{ $t('Audience_Segment_Create_Button') }}
          </el-button>
        </router-link>
        <el-button
          type="primary"
          icon="el-icon-plus"
          :disabled="!isRollingSegmentsCreationAvailable"
          @click="createRollingSegment"
          v-if="
            hasFeature('rollingSegments') &&
              currentRoute === 'AudienceRollingSegmentList'
          "
        >
          {{ $t('Audience_Segment_CreateRolling_Button') }}
        </el-button>
        <el-button
          @click="createSuppressionListDialog"
          type="primary"
          icon="el-icon-plus"
          v-if="currentRoute === 'AudienceSuppressionList'"
        >
          {{ $t('Lists_CreateSuppressionList') }}
        </el-button>

        <el-button
          v-if="currentRoute === 'AudienceAutomationsList'"
          @click="$router.push(automationCreateRoute)"
          type="primary"
          icon="el-icon-plus"
        >
          {{ $t('Automations_Button_CreateAutomation') }}
        </el-button>
      </template>
    </page-header-new>
    <page-tabs :tabs="availableTabs"></page-tabs>
  </page>
</template>

<script>
import PageHeaderNew from '@/common/components/page/PageHeaderNew'
import Page from '@/common/components/page/Page'
import PageTabs from '@/common/components/page/PageTabs'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import { EventBus } from '@/core/EventBus'
import RestApi from '@/core/services/RestApi'
import { integrationsTypes } from '@/modules/integrations/enum/IntegrationsEnum'
import { $hasFeature } from '@/core/utils/VueAcl'
import { mapGetters } from 'vuex'

export default {
  name: 'Audiences',
  components: { PageTabs, PageHeaderNew, Page },
  data() {
    return {
      isSportradar: false,
      rollingSegmentsCount: null
    }
  },
  watch: {
    $route(to, from) {
      if (to.name === 'AudienceRollingSegmentList') {
        this.rollingSegmentsCount = null
      }
    }
  },
  mounted() {
    EventBus.$on('get-rolling-segments-count', count => {
      this.rollingSegmentsCount = count
    })
    RestApi(
      projectEndpoint({
        method: 'get',
        url: `integrations`,
        params: {
          type: integrationsTypes.sportradar,
          active: 1
        }
      })
    )
      .then(({ data }) => {
        this.isSportradar = !!data.length
      })
      .catch(error => {
        this.$message({
          message: error.message,
          type: 'error'
        })
      })
  },
  destroyed() {
    EventBus.$off('get-rolling-segments-count')
  },
  methods: {
    createSuppressionListDialog() {
      EventBus.$emit('create-suppression-list')
    },
    createRollingSegment() {
      if (
        this.rollingSegmentsCount >=
        this.$store.state.app.project.max_rolling_segments_count
      ) {
        this.$message({
          message: this.$t('Audiences_RollingSegment_Max_Count_Warning', {
            count: this.$store.state.app.project.max_rolling_segments_count
          }),
          type: 'error'
        })
      } else {
        this.$router.push(this.rollingSegmentCreateRoute)
      }
    },
    createUserListDialog() {
      EventBus.$emit('create-user-list')
    },
    createOptimoveListDialog() {
      EventBus.$emit('create-optimove-list')
    },
    hasFeature(feature) {
      return $hasFeature(feature)
    }
  },
  computed: {
    ...mapGetters(['isOptimoveAvailable']),

    isRollingSegmentsCreationAvailable() {
      return this.rollingSegmentsCount !== null
    },
    segmentCreateRoute() {
      return projectRoute({
        name: 'AudienceSegmentCreate'
      })
    },
    rollingSegmentCreateRoute() {
      return projectRoute({
        name: 'AudienceRollingSegmentCreate'
      })
    },
    automationCreateRoute() {
      return projectRoute({
        name: 'AudienceAutomationCreate'
      })
    },
    currentRoute() {
      return this.$route.name
    },
    readonly() {
      return !this.$can('Segment::Create')
    },
    availableTabs() {
      if (this.$isRole('consent-admin')) {
        return [
          {
            title: this.$t('Audiences_SegmentListTab'),
            route: projectRoute({ name: 'AudienceSegmentList' })
          }
        ]
      }

      let tabs = [
        {
          title: this.$t('Audiences_SegmentListTab'),
          route: projectRoute({ name: 'AudienceSegmentList' })
        },
        {
          title: this.$t('Audiences_UserListTab'),
          route: projectRoute({ name: 'AudienceUserList' })
        },
        {
          title: this.$t('Audiences_SuppressionListTab'),
          route: projectRoute({ name: 'AudienceSuppressionList' })
        }
      ]

      if (this.hasFeature('rollingSegments')) {
        // add rolling segments after segments tab
        tabs.splice(1, 0, {
          title: this.$t('Audiences_RollingSegmentListTab'),
          route: projectRoute({ name: 'AudienceRollingSegmentList' })
        })
      }

      if (
        this.$store.state.app.project.company.segment_sharing &&
        this.$store.state.app.project.company.projects_count > 1
      ) {
        tabs.push({
          title: this.$t('Audiences_SegmentSharedTab'),
          route: projectRoute({ name: 'AudienceSegmentSharedList' })
        })
      }

      if (this.isSportradar) {
        tabs.push({
          title: this.$t('Audiences_SportradarListTab'),
          route: projectRoute({ name: 'AudienceSportradarList' })
        })
      }

      if (
        this.hasFeature('enableGamification') &&
        this.isOptimoveListsAvailable
      ) {
        tabs.push({
          title: this.$t('Audiences_OptimoveListTab'),
          route: projectRoute({ name: 'AudienceOptimoveList' })
        })
      }

      return tabs
    },
    breadcrumbs() {
      return [
        {
          label: 'Data'
        },
        {
          label: 'Audiences'
        }
      ]
    },
    isOptimoveListsAvailable() {
      return (
        this.isOptimoveAvailable &&
        this.$store.state.app.integrations.optimoveWithListsChannel
      )
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/modules/audiences/pages/Audiences.vue